---
id: libs-utils-src-session-timeout-doc
title: Session Timeout
owner: Luc Lemerez
status: Active
lastReviewed: 2026-01-27
visibility: internal
tags:
  - utils
  - session
  - auth
---

Tracks client-side activity and determines when inactivity should end a session.

## Product Overview

Used by app shells to determine when an idle session should be logged out.

### Primary Workflow
App shells initialize the activity listeners, track user interactions, and
periodically check whether the inactivity window has elapsed to enforce session
timeouts.

### Business Logic
The session is considered expired when the time since last recorded activity
exceeds the configured inactivity window, which is currently set to four hours.

### Outcomes
This utility standardizes idle session handling across apps and enables
consistent enforcement of inactivity timeouts.

### Out of Scope
Server-side session invalidation and authentication flows are handled outside
this utility.

## Technical Overview

The implementation lives in `libs/utils/src/session-timeout/index.ts` and
exposes helpers for initializing listeners and evaluating expiry.

### Entry Points
`updateLastActive` writes timestamps to local storage, `initActivityListeners`
registers user activity events, and `hasSessionExpired` checks the inactivity
limit.

### Key Modules
`SESSION_INACTIVITY_LIMIT` defines the timeout window, and `STORAGE_KEY`
controls the localStorage key used for persistence.

### Data and Services
The utility relies on browser localStorage and the current clock to compute
elapsed inactivity time.

### Integrations and Side Effects
`initActivityListeners` registers global event handlers for interaction events
such as mouse movement, keypresses, clicks, scrolls, and touch events.

### Constraints and Risks
Clearing localStorage or blocking event listeners can cause false session
expirations or missed activity updates.

## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-23: Initial documentation.
